import React, { useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material"
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro"
import { formatUtcToLocal } from "../../helper/utils"
import type { InFileDuplicateGroup } from "../../Pages/OpenTaxReport/otrImportDuplicateKey"

export interface MatchedExistingOtrItem {
  importRowIndex?: number
  newOpenTaxReportId?: number
  customerPolicyId?: number
  matchSource?: string
  policyNumber?: string
  invoiceNumber?: string
  stateId?: number
  /** Matched OTR row state (postal abbreviation from States). */
  state?: string
  /** State value from import payload (Excel). */
  importStateCode?: string
  client?: string
  transactionType?: string
  taxType?: string
  grossPremium?: string
  taxAmt?: string | number
  invDate?: string
  effectiveDate?: string
  expirationDate?: string
}

interface OTRImportInFileDuplicateDialogProps {
  open: boolean
  groups: InFileDuplicateGroup[]
  onSkipDuplicates: () => void
  onImportAll: () => void
  onCancel: () => void
}

export function OTRImportInFileDuplicateDialog({
  open,
  groups,
  onSkipDuplicates,
  onImportAll,
  onCancel,
}: OTRImportInFileDuplicateDialogProps) {
  const totalDupRows = useMemo(
    () => groups.reduce((s, g) => s + g.indices.length, 0),
    [groups]
  )

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Duplicate rows in import file</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {groups.length} group(s) of duplicate transactions ({totalDupRows} rows). Each group shares the same
          policy, invoice, state, client, tax line, amounts, and dates. Choose whether to import only the first row
          in each group or import all rows.
        </Typography>
        <Box component="ul" sx={{ pl: 2, maxHeight: 320, overflow: "auto" }}>
          {groups.map((g, gi) => (
            <li key={gi}>
              <Typography variant="body2">
                Rows {g.rowLabels.join(", ")} — same transaction ({g.indices.length} copies)
              </Typography>
            </li>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel import
        </Button>
        <Button onClick={onImportAll} color="warning" variant="outlined">
          Import all
        </Button>
        <Button onClick={onSkipDuplicates} variant="contained" color="primary">
          Skip duplicate rows (keep first)
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface OTRImportExistingDuplicateDialogProps {
  open: boolean
  matches: MatchedExistingOtrItem[]
  onSkipMatched: () => void
  onImportAll: () => void
  onCancel: () => void
}

export function OTRImportExistingDuplicateDialog({
  open,
  matches,
  onSkipMatched,
  onImportAll,
  onCancel,
}: OTRImportExistingDuplicateDialogProps) {
  const matchSourceLabel = (raw: string | undefined) => {
    if (raw === "OTR") return "Pending OTR"
    if (raw === "FilingOutstanding") return "Outstanding filing"
    if (raw === "FilingReadyToFile") return "Ready to file"
    if (raw === "FilingCompleted") return "Completed"
    return raw?.trim() ? raw : "—"
  }

  const rows = useMemo(() => {
    const fmtLocal = (v: string | null | undefined) =>
      v != null && String(v).trim() !== "" ? formatUtcToLocal(v, "MM/DD/YYYY", true) : ""
    return matches.map((item, index) => {
      const src = item.matchSource ?? ""
      const otrId = item.newOpenTaxReportId ?? ""
      const cpId = item.customerPolicyId ?? ""
      const rowNum = item.importRowIndex ?? ""
      return {
        id: `dup-${src}-${otrId}-${cpId}-${rowNum}-${index}`,
        importRowIndex: rowNum,
        matchSourceLabel: matchSourceLabel(item.matchSource),
        policyNumber: item.policyNumber ?? "",
        invoiceNumber: item.invoiceNumber ?? "",
        state: item.state ?? item.importStateCode ?? "",
        client: item.client ?? "",
        transactionType: item.transactionType ?? "",
        taxType: item.taxType ?? "",
        grossPremium: item.grossPremium ?? "",
        taxAmt: item.taxAmt != null && item.taxAmt !== "" ? String(item.taxAmt) : "",
        invDate: fmtLocal(item.invDate),
        effectiveDate: fmtLocal(item.effectiveDate),
        expirationDate: fmtLocal(item.expirationDate),
      }
    })
  }, [matches])

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "importRowIndex", headerName: "Import row", width: 90 },
      { field: "matchSourceLabel", headerName: "Match source", width: 150 },
      { field: "policyNumber", headerName: "Policy #", flex: 1, minWidth: 120 },
      { field: "state", headerName: "State", width: 72 },
      { field: "invoiceNumber", headerName: "Invoice #", width: 110 },
      { field: "client", headerName: "Client", flex: 1, minWidth: 140 },
      { field: "transactionType", headerName: "Transaction Type", width: 130 },
      { field: "taxType", headerName: "Tax Type", width: 120 },
      { field: "grossPremium", headerName: "Gross Premium", width: 110 },
      { field: "taxAmt", headerName: "Tax Amt", width: 90 },
      { field: "invDate", headerName: "Inv Date", width: 100 },
      { field: "effectiveDate", headerName: "Effective Date", width: 110 },
      { field: "expirationDate", headerName: "Expiration Date", width: 110 },
    ],
    []
  )

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth>
      <DialogTitle>Matches existing records</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {matches.length} import row(s) match a pending Open Tax Report row and/or a transaction already on the
          filing queue (Outstanding, Ready to File, or Completed) with the same business key. You can skip those
          import rows or import all (which may create near-duplicate records).
        </Typography>
        <Box sx={{ height: 360, width: "100%" }}>
          <DataGridPro rows={rows} columns={columns} density="compact" disableSelectionOnClick />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel import
        </Button>
        <Button onClick={onImportAll} color="warning" variant="outlined">
          Import all
        </Button>
        <Button onClick={onSkipMatched} variant="contained" color="primary">
          Skip matching rows
        </Button>
      </DialogActions>
    </Dialog>
  )
}
